"use client"
import { Dialog, DialogContent, DialogActions, Button, Typography, Box, IconButton } from "@mui/material"
import {
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
} from "@mui/icons-material"
import { styled } from "@mui/material/styles"

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: theme.spacing(2),
    padding: theme.spacing(1),
    minWidth: 320,
    maxWidth: 400,
  },
  "& .MuiBackdrop-root": {
    backgroundColor: "rgba(0, 0, 0, 0.3)", // Reduzir opacidade de 0.5 para 0.3
  },
}))

const DialogHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: theme.spacing(2),
}))

const IconContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 60,
  height: 60,
  borderRadius: "50%",
  margin: "0 auto",
  marginBottom: theme.spacing(2),
}))

const BlockIconContainer = styled(IconContainer)(({ theme }) => ({
  backgroundColor: theme.palette.error.light,
  color: theme.palette.error.main,
}))

const UnblockIconContainer = styled(IconContainer)(({ theme }) => ({
  backgroundColor: theme.palette.success.light,
  color: theme.palette.success.main,
}))

interface LockUnlockConfirmationDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  isBlocked: boolean
  deviceName: string
  loading?: boolean
}

const LockUnlockConfirmationDialog = ({ open, onClose, onConfirm, isBlocked, deviceName, loading = false }) => {
  const action = isBlocked ? "desbloquear" : "bloquear"
  const actionTitle = isBlocked ? "Desbloquear Veículo" : "Bloquear Veículo"
  const actionDescription = isBlocked
    ? "Esta ação irá desbloquear o veículo, permitindo que ele seja ligado normalmente."
    : "Esta ação irá bloquear o veículo, impedindo que ele seja ligado."

  const handleConfirm = () => {
    onConfirm()
    onClose()
  }

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="sm" fullWidth disableEscapeKeyDown={loading}>
      <DialogContent>
        <DialogHeader>
          <Typography variant="h6" component="h2" fontWeight="bold">
            {actionTitle}
          </Typography>
          <IconButton onClick={onClose} size="small" disabled={loading} sx={{ color: "text.secondary" }}>
            <CloseIcon />
          </IconButton>
        </DialogHeader>

        {isBlocked ? (
          <UnblockIconContainer>
            <LockOpenIcon fontSize="large" />
          </UnblockIconContainer>
        ) : (
          <BlockIconContainer>
            <LockIcon fontSize="large" />
          </BlockIconContainer>
        )}

        <Box textAlign="center" mb={2}>
          <Typography variant="body1" fontWeight="medium" gutterBottom>
            {deviceName}
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            {actionDescription}
          </Typography>

          {!isBlocked && (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              gap={1}
              sx={{
                backgroundColor: "warning.light",
                color: "warning.dark",
                padding: 1.5,
                borderRadius: 1,
                mt: 2,
              }}
            >
              <WarningIcon fontSize="small" />
              <Typography variant="caption" fontWeight="medium">
                Atenção: Esta ação pode afetar a segurança do veículo
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ padding: 2, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" disabled={loading} sx={{ minWidth: 100 }}>
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color={isBlocked ? "success" : "error"}
          disabled={loading}
          sx={{ minWidth: 100 }}
          startIcon={
            loading ? (
              <div
                style={{
                  width: 16,
                  height: 16,
                  border: "2px solid currentColor",
                  borderTop: "2px solid transparent",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                }}
              />
            ) : isBlocked ? (
              <LockOpenIcon />
            ) : (
              <LockIcon />
            )
          }
        >
          {loading ? "Enviando..." : `${action.charAt(0).toUpperCase() + action.slice(1)}`}
        </Button>
      </DialogActions>
    </StyledDialog>
  )
}

export default LockUnlockConfirmationDialog
